<template>
    <div>
        <hts-router-drawer
            :title="$t('labels.taskInformation')"
            size="large">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.partner_task_completion_time_add)"
                    color="main"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    @click="addCompletionTime">
                    {{ $t('labels.addCompletionTime') }}
                </hts-form-button>
            </div>

            <hts-data-table
                v-if="checkPermission(layoutPermissions.partner_task_completion_time_management)"
                ref="dataTables"
                :headers="headers"
                :server="server"
                store-name="cooperatorDataTable"
                store-key="CooperatorTaskCompleteTime"
                :use-archive="useArchive"
                :use-restore="useRestore"
                use-selection />
        </hts-router-drawer>
        <add-task-completion-time
            v-if="checkPermission(layoutPermissions.partner_task_completion_time_add)"
            ref="addTaskCompletionTime"
            @refresh-tables="refreshTables()" />
        <edit-task-completion-time
            v-if="checkPermission(layoutPermissions.partner_task_completion_time_edit)"
            ref="editTaskCompletionTime"
            @refresh-tables="refreshTables()" />
        <detail-task-completion-time
            v-if="checkPermission(layoutPermissions.partner_task_completion_time_management)"
            ref="detailTaskCompletionTime" />

        <hts-modal
            :show="showPopupDelete"
            :title="$t('labels.deleteCompletionTime')"
            type="confirm"
            :content="$t('labels.detailDeleteCompletionTime')"
            @close="closePopupDelete"
            @confirm="handleDelete" />
    </div>
</template>

<script setup lang="ts">
import AddTaskCompletionTime from './task-completion-time/ignore/add.vue'
import DetailTaskCompletionTime from './task-completion-time/ignore/detail.vue'
import EditTaskCompletionTime from './task-completion-time/ignore/edit.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
// import { $api, $urlSystem } from '#imports'
import actions from '~/pages/trucker-[id]/partner/-components/actions.vue'
const cooperatorId = computed(() => useRoute().params.idpartner)
const { $formatters } = useNuxtApp()

const addTaskCompletionTime = ref()
const editTaskCompletionTime = ref()
const detailTaskCompletionTime = ref()
const dataTables = ref()
const showPopupDelete = ref(false)
const completionTimeId = ref('')
const vehicleCategories = ref<null | Array<any>>()

const server = {
  method: 'post',
  url: '/partner/task-completion-time/' + cooperatorId.value + '/list'
}

const addCompletionTime = () => {
  if (addTaskCompletionTime.value) {
    addTaskCompletionTime.value.open()
  }
}

const openEdit = (completionTime: any) => {
  if (editTaskCompletionTime.value) {
    editTaskCompletionTime.value.open(completionTime._id)
  }
}

const openViewDetail = (completionTime: any) => {
  if (detailTaskCompletionTime.value) {
    detailTaskCompletionTime.value.open(completionTime._id)
  }
}

const openDelete = (completionTime: any) => {
  if (!completionTime) {
    return null
  }
  showPopupDelete.value = true
  completionTimeId.value = completionTime._id
}

const closePopupDelete = () => {
  showPopupDelete.value = false
  completionTimeId.value = ''
}

const handleDelete = () => {
  const api = $urlPartner.partner.taskCompletionTime.delete
  $api(api, { body: { ids: [completionTimeId.value] }, uriParams: { cooperatorId: cooperatorId.value } }).then((data) => {
    if (!data) {
      return true
    }
    const responseData: any = data.data.value
    if (!responseData) {
      return true
    }
    if (responseData.success) {
      refreshTables()
      process.client && $toast().success($t('labels.deleteSuccess'))
      closePopupDelete()
    }
  })
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const headers = computed(() => {
  let headersDefault: any = [
    {
      field: 'nodeName',
      headerName: $t('labels.node')
    },
    {
      field: 'taskInfo.taskName',
      headerName: $t('labels.task')
    },
    {
      field: 'taskInfo.businessName',
      headerName: $t('labels.business')
    }
  ]

  const headersRemooc: any = [
    ...contTypes.map((type) => {
      return {
        headerName: type.label,
        sortable: false,
        valueGetter: (params: any) => {
          try {
            const taskType = params.data.taskType
            if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
              return ''
            }

            const completionTimes = params.data.completionTimes

            if (!completionTimes) {
              return ''
            }

            const time = completionTimes.contCategory[type.value].averageTime

            if (!time) {
              return ''
            }

            return time + ` ${$t('labels.hour')}`
          } catch (e) {
            return ''
          }
        }
      }
    })
  ]

  const headersVehicle = () => {
    if (!vehicleCategories.value) {
      return []
    }
    return [
      ...vehicleCategories?.value?.map((item: any) => {
        const id = item._id
        return {
          headerName: item.name,
          sortable: false,
          valueGetter: (params: any) => {
            try {
              const taskType = params.data.taskType
              if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
                return ''
              }

              const completionTimes = params.data.completionTimes

              if (!completionTimes) {
                return ''
              }

              const time = completionTimes.vehicleCategory[id].averageTime

              if (!time) {
                return ''
              }

              return time + ` ${$t('labels.hour')}`
            } catch (e) {
              return ''
            }
          }
        }
      })
    ]
  }

  const headersAction = [
    {
      field: 'createdAt',
      headerName: $t('labels.createdAt'),
      sortable: true,
      valueGetter: (params: any) => {
        return $formatters.datetime(params.data.createdAt)
      }
    },
    {
      field: 'updatedAt',
      headerName: $t('labels.updatedAt'),
      sortable: true,
      valueGetter: (params: any) => {
        return $formatters.datetime(params.data.updatedAt)
      }
    },
    {
      field: 'action',
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      lockPinned: true,
      maxWidth: 120,
      headerName: $t('labels.action'),
      cellStyle: { overflow: 'visible' },
      cellRenderer: actions,
      cellRendererParams: {
        storeKey: 'cooperatorDataTable',
        storeName: 'CooperatorTaskCompleteTime',
        items: [
          {
            icon: 'hi-eye-icon',
            label: $t('labels.detail'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.partner_task_completion_time_management),
            click: openViewDetail
          },
          {
            icon: 'hi-pencil-square-icon',
            label: $t('labels.edit'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.partner_task_completion_time_edit),
            click: openEdit
          },
          {
            icon: 'hi-trash-icon',
            label: $t('labels.delete'),
            class: 'text-error-600 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.partner_task_completion_time_delete),
            click: openDelete,
            isArchive: true
          }
        ]
      }
    }
  ]

  headersDefault = headersDefault.concat(headersVehicle()).concat(headersRemooc).concat(headersAction)
  return headersDefault
})

onBeforeMount(() => {
  const api = $urlSystem.vehicle.category.getAll

  $api(api).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      vehicleCategories.value = resData.data || []
    }
  })
})

const contTypes = [
  {
    value: 20,
    label: $t('labels.Container20F')
  },
  {
    value: 40,
    label: $t('labels.Container40F')
  },
  {
    value: 45,
    label: $t('labels.Container45F')
  }
]

const useArchive = { api: $urlPartner.partner.taskCompletionTime.archive, permissions: checkPermission(layoutPermissions.partner_task_completion_time_archive) }
const useRestore = { api: $urlPartner.partner.taskCompletionTime.restore, permissions: checkPermission(layoutPermissions.partner_task_completion_time_restore) }
</script>
